import { action, atom } from '@reatom/framework'

import { AsyncStateN, pipe, Record, Result } from '@blade/std-lib'
import { currencyRegistry, type CurrencyModel } from '@root/entities/currency'
import { Network, selectedNetworkAtom } from '@root/entities/network'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'

import type { Error } from '@root/shared'

export const fetchDefaultCurrenciesRequestAction = action(
  async (ctx, { network }: { network: Network.Network }) => {
    const { tradeApiClient } = ctx.get(dependenciesAtom)

    const result = await ctx.schedule(() =>
      apiRequest(ctx, tradeApiClient.services.servicesDefaultAssetsList, [
        {
          network: Network.toDto(network),
        },
      ]),
    )

    const mappedResult = pipe(
      result,
      Result.mapError(ApiError.fallbackToUnknown),
    )

    return mappedResult
  },
  'fetchDefaultCurrenciesRequestAction',
)

type ByNetwork = Record<
  Network.Network,
  AsyncStateN.AsyncStateN<Error.UnknownError, Array<CurrencyModel>>
>

export const defaultCurrenciesByNetworkAtom = atom<ByNetwork>(
  Network.byNetworkRecord(AsyncStateN.notInitiated),
  'defaultCurrenciesByNetworkAtom',
)
export const defaultCurrenciesStateAtom = atom((ctx) => {
  const network = ctx.spy(selectedNetworkAtom)
  const state = ctx.spy(defaultCurrenciesByNetworkAtom)[network]

  return state
}, 'defaultCurrenciesStateAtom')

export const defaultCurrenciesAtom = atom((ctx) => {
  return ctx.spy(defaultCurrenciesStateAtom).value ?? []
}, 'defaultCurrenciesAtom')

export const loadDefaultCurrenciesAction = action(async (ctx) => {
  const network = ctx.get(selectedNetworkAtom)
  const state = ctx.get(defaultCurrenciesByNetworkAtom)[network]

  if (AsyncStateN.isSuccess(state)) {
    return
  }

  defaultCurrenciesByNetworkAtom(ctx, (state) =>
    pipe(state, Record.set(network, AsyncStateN.loading())),
  )

  const result = await ctx.schedule(() =>
    fetchDefaultCurrenciesRequestAction(ctx, { network: network }),
  )

  const mappedResult = pipe(
    result,
    Result.map((it) =>
      it.map((pair) => currencyRegistry.resolveFromTokenMeta(ctx, pair.quote)),
    ),
  )
  defaultCurrenciesByNetworkAtom(ctx, (state) =>
    pipe(state, Record.set(network, AsyncStateN.fromResult(mappedResult))),
  )
}, 'loadDefaultCurrenciesAction')
